import { useEffect, useRef, useState } from "react";
import { FileText, Plus, Download, ExternalLink, Pencil, Trash2, File } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, assetSrc, formatSize } from "../../lib/api";
import { DOC_TYPES, DOC_TYPE_LABEL, fileExtension } from "../../lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import ConfirmDelete from "../ConfirmDelete";

const inputCls =
  "w-full h-12 px-4 rounded-xl border border-zinc-200 bg-white text-base placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 transition-shadow";

export default function DocumentsTab({ machine, isAdmin }) {
  const [docs, setDocs] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", doc_type: "manual" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/machines/${machine.id}/documents`).then(({ data }) => setDocs(data));
  useEffect(() => { load(); }, [machine.id]);

  const openUpload = () => {
    setForm({ name: "", doc_type: "manual" });
    setFile(null);
    setUploadOpen(true);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Selecciona un archivo"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("doc_type", form.doc_type);
      fd.append("file", file);
      await api.post(`/machines/${machine.id}/documents`, fd);
      toast.success("Documento subido");
      setUploadOpen(false);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/documents/${editing.id}`, { name: form.name, doc_type: form.doc_type });
      toast.success("Documento actualizado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (doc) => {
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Documento eliminado");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div data-testid="documents-tab">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-xl font-semibold tracking-tight">Documentos</h2>
        {isAdmin && (
          <button
            onClick={openUpload}
            data-testid="upload-document-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 active:scale-95 transition-[background-color,transform] duration-200"
          >
            <Plus size={15} /> Subir documento
          </button>
        )}
      </div>

      {docs === null ? (
        <div className="flex justify-center py-12">
          <div className="h-7 w-7 rounded-full border-2 border-zinc-300 border-t-zinc-900 animate-spin" />
        </div>
      ) : docs.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-2xl border border-dashed border-zinc-200" data-testid="documents-empty">
          <File className="mx-auto text-zinc-300 mb-3" size={32} strokeWidth={1.5} />
          <p className="text-zinc-500 text-sm">Todavía no hay documentos.</p>
          {isAdmin && <p className="text-zinc-400 text-sm mt-1">Sube el manual, esquema eléctrico o ficha técnica.</p>}
        </div>
      ) : (
        <div className="space-y-2.5">
          {docs.map((d) => (
            <div
              key={d.id}
              data-testid={`doc-item-${d.id}`}
              className="flex items-center gap-4 bg-white rounded-2xl border border-zinc-200/80 card-shadow p-4"
            >
              <span className="h-11 w-11 rounded-xl bg-zinc-100 flex items-center justify-center text-zinc-500 shrink-0">
                <FileText size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{d.name}</p>
                <p className="text-sm text-zinc-400">
                  {DOC_TYPE_LABEL[d.doc_type] || "Otros"} · {fileExtension(d.original_filename) || "PDF"}
                  {d.size ? ` · ${formatSize(d.size)}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => window.open(assetSrc(d), "_blank")}
                  title="Abrir"
                  data-testid={`doc-open-${d.id}`}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                >
                  <ExternalLink size={16} />
                </button>
                <a
                  href={assetSrc(d)}
                  download={d.original_filename || d.name}
                  title="Descargar"
                  data-testid={`doc-download-${d.id}`}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                >
                  <Download size={16} />
                </a>
                {isAdmin && (
                  <>
                    <button
                      onClick={() => { setForm({ name: d.name, doc_type: d.doc_type }); setEditing(d); }}
                      title="Editar"
                      data-testid={`doc-edit-${d.id}`}
                      className="h-9 w-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                    >
                      <Pencil size={16} />
                    </button>
                    <ConfirmDelete
                      testid={`doc-delete-${d.id}`}
                      title={`¿Eliminar «${d.name}»?`}
                      description="El archivo se eliminará de forma permanente. Esta acción no se puede deshacer."
                      onConfirm={() => handleDelete(d)}
                    />
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="rounded-3xl sm:max-w-md" data-testid="upload-doc-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Subir documento</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4 pt-2">
            <input
              className={inputCls}
              placeholder="Nombre del documento"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
              data-testid="doc-name-input"
            />
            <select
              className={inputCls}
              value={form.doc_type}
              onChange={(e) => setForm((f) => ({ ...f, doc_type: e.target.value }))}
              data-testid="doc-type-select"
            >
              {DOC_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <label
              className="flex items-center justify-center gap-3 h-24 rounded-2xl border border-dashed border-zinc-300 cursor-pointer hover:border-zinc-400 transition-colors text-sm text-zinc-500"
              data-testid="doc-file-drop"
            >
              <FileText size={18} className="text-zinc-400" />
              {file ? file.name : "PDF, Word, Excel o imagen"}
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="doc-file-input"
              />
            </label>
            <button
              type="submit"
              disabled={saving}
              data-testid="doc-upload-submit"
              className="w-full h-12 rounded-full bg-zinc-900 text-white font-medium hover:bg-zinc-800 active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-50"
            >
              {saving ? "Subiendo…" : "Subir documento"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-3xl sm:max-w-md" data-testid="edit-doc-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Editar documento</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 pt-2">
            <input
              className={inputCls}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
              data-testid="edit-doc-name-input"
            />
            <select
              className={inputCls}
              value={form.doc_type}
              onChange={(e) => setForm((f) => ({ ...f, doc_type: e.target.value }))}
              data-testid="edit-doc-type-select"
            >
              {DOC_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <button
              type="submit"
              disabled={saving}
              data-testid="edit-doc-submit"
              className="w-full h-12 rounded-full bg-zinc-900 text-white font-medium hover:bg-zinc-800 active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-50"
            >
              {saving ? "Guardando…" : "Guardar cambios"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
